import React, { useRef, useState, useEffect, useCallback } from 'react';

// One horizontal carousel: prev/next arrows (Device-Finder style) + expand-to-grid.
// `device` is the model this group was matched to — shown as a static "Κοπή για"
// line on device-specific items so their cards line up with the universal ones.
function Carousel({ items, adding, onAdd, device = '', moreUrl = '', moreLabel = '' }) {
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const [overflow, setOverflow] = useState(false);
  const [dev, setDev] = useState({}); // universal item id -> chosen device slug

  // Device a universal item will be cut for (default: first option).
  const chosenDev = (cs) => {
    const opts = (cs.deviceOptions || []).map((o) => o.slug);
    const sel = dev[cs.id];
    // Fall back to the first option if the stored choice is no longer offered
    // (e.g. its model was just added and dropped out of deviceOptions).
    return sel && opts.indexOf(sel) !== -1 ? sel : (opts[0] || '');
  };

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 2);
    setCanNext(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
    if (!expanded) setOverflow(el.scrollWidth > el.clientWidth + 2);
  }, [expanded]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => { el.removeEventListener('scroll', update); window.removeEventListener('resize', update); };
  }, [update, items.length, expanded]);

  const scroll = (dir) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: 'smooth' });
  };

  return (
    <div className={`cart-cross__carousel ${expanded ? 'is-expanded' : ''}`}>
      <div className="cart-cross__viewport">
        {!expanded && canPrev && (
          <button type="button" className="cart-cross__nav cart-cross__nav--prev" aria-label="Προηγούμενα" onClick={() => scroll(-1)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
          </button>
        )}
        {!expanded && canNext && (
          <button type="button" className="cart-cross__nav cart-cross__nav--next" aria-label="Επόμενα" onClick={() => scroll(1)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 18l6-6-6-6" /></svg>
          </button>
        )}
        <div className="cart-cross__list" ref={ref}>
          {items.map((cs) => (
            <div className={`cart-cross__item ${cs.universal ? 'is-universal' : ''}`} key={cs.id}>
              <a className="cart-cross__thumb" href={cs.url}>
                {cs.discountPct && <span className="cart-cross__badge">-{cs.discountPct}%</span>}
                {cs.universal && (
                  <span className="cart-cross__universal" title="Κόβεται στα μέτρα της συσκευής σου">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="6" cy="6" r="3" /><circle cx="6" cy="18" r="3" /><line x1="20" y1="4" x2="8.12" y2="15.88" /><line x1="14.47" y1="14.48" x2="20" y2="20" /><line x1="8.12" y1="8.12" x2="12" y2="12" /></svg>
                    Universal
                  </span>
                )}
                {cs.image ? <img src={cs.image} alt="" loading="lazy" /> : null}
              </a>
              <a className="cart-cross__name" href={cs.url} title={cs.name}>{cs.name}</a>
              <div className="cart-cross__price">
                {cs.regular && <del className="cart-cross__was">{cs.regular}</del>}
                <span className="cart-cross__now">{cs.price}</span>
              </div>
              {cs.universal && cs.deviceOptions && cs.deviceOptions.length > 0 ? (
                <label className="cart-cross__cut">
                  <span className="cart-cross__cut-label">Κοπή για:</span>
                  <select value={chosenDev(cs)} onChange={(e) => setDev((d) => ({ ...d, [cs.id]: e.target.value }))}>
                    {cs.deviceOptions.map((o) => <option key={o.slug} value={o.slug}>{o.name}</option>)}
                  </select>
                </label>
              ) : device ? (
                <div className="cart-cross__cut cart-cross__cut--fixed">
                  <span className="cart-cross__cut-label">Ταιριάζει σε:</span>
                  <span className="cart-cross__cut-value" title={device}>{device}</span>
                </div>
              ) : null}
              <button
                type="button"
                className={`cn-btn cn-btn--ghost cart-cross__add ${adding[cs.id] ? 'is-adding' : ''}`}
                disabled={!!adding[cs.id]}
                onClick={() => onAdd(cs, cs.universal ? chosenDev(cs) : undefined)}
              >
                {adding[cs.id] ? (<><span className="cart-spinner cart-cross__spin" aria-hidden="true" />Προστίθεται…</>) : '+ Προσθήκη'}
              </button>
            </div>
          ))}
        </div>
      </div>
      {(overflow || expanded) && (
        <div className="cart-cross__foot">
          <button type="button" className="cart-cross__toggle" onClick={() => setExpanded((v) => !v)}>
            <span>{expanded ? 'Λιγότερα' : `Εμφάνιση όλων (${items.length})`}</span>
            <span className={`cart-cross__chev ${expanded ? 'up' : ''}`} aria-hidden="true">⌄</span>
          </button>
          {/* Once expanded the shopper has seen the whole (capped) set — offer the
              full category archive as the way out. */}
          {expanded && moreUrl && (
            <a className="cart-cross__all" href={moreUrl}>
              {moreLabel || 'Δες όλη την κατηγορία'}
              <span className="cart-cross__all-arrow" aria-hidden="true">→</span>
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// A promo block (one grey box). Accepts either a flat `items` list or `groups`
// ([{ device, items }]) — when the cart has the same category for different
// models, each model gets a labelled sub-carousel inside the SAME box.
export default function CrossSells({ items, groups, adding, onAdd, section = '', title = 'Πρόσθεσε και προστασία οθόνης', eyebrow = 'Προτεινόμενα για σένα', offer = '', moreUrl = '' }) {
  const list = (groups && groups.length)
    ? groups.filter((g) => g && g.items && g.items.length)
    : ((items && items.length) ? [{ device: '', items }] : []);
  const itemCount = list.reduce((n, g) => n + (g.items ? g.items.length : 0), 0);

  // Exposure: fire ONCE when this promo block actually scrolls into view (the blocks
  // sit below the cart, so mount ≠ seen). This is the denominator for "% who used it".
  const rootRef = useRef(null);
  const shownRef = useRef(false);
  useEffect(() => {
    const el = rootRef.current;
    if (!el || shownRef.current || !section) return undefined;
    const cap = () => {
      if (shownRef.current) return;
      shownRef.current = true;
      try {
        if (window.posthog && typeof window.posthog.capture === 'function') {
          window.posthog.capture('cart_crosssell_shown', {
            section, section_title: title, item_count: itemCount, location: 'cart_next',
          });
        }
      } catch (e) { /* noop */ }
    };
    if (typeof IntersectionObserver === 'undefined') { cap(); return undefined; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { cap(); io.disconnect(); } });
    }, { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
  }, [section, title, itemCount]);

  if (!list.length) return null;
  const multiModel = list.length > 1;
  // Tag every add coming out of this block with its section for the "used it" event.
  const addFromSection = (cs, dev) => onAdd(cs, dev, section);

  return (
    <section className="cart-cross" ref={rootRef} id={section ? `cx-${section}` : undefined}>
      <div className="cart-cross__head">
        <span className="cart-cross__eyebrow">{eyebrow}</span>
        <h2 className="cart-cross__title">{title}</h2>
        {offer && (
          <span className="cart-cross__offer">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>
            {offer}
          </span>
        )}
      </div>

      {list.map((g, i) => (
        <div className="cart-cross__group" key={g.device || i}>
          {multiModel && g.device && (
            <h3 className="cart-cross__model">
              {g.combined ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="9" /></svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="7" y="2" width="10" height="20" rx="2" /><line x1="11" y1="18" x2="13" y2="18" /></svg>
              )}
              {g.device}
            </h3>
          )}
          <Carousel items={g.items} adding={adding} onAdd={addFromSection} device={g.combined ? '' : (g.device || '')} moreUrl={moreUrl} />
        </div>
      ))}
    </section>
  );
}
